import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Gift, Copy, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Compact "Refer a Friend" card shown on the student dashboard.  Displays
// the caller's referral code + a share link and shows how many free bonus
// months they have already earned.
export default function ReferralCard() {
  const [info, setInfo] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await fetch(`${API}/api/referral/my`, {
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (r.ok) setInfo(await r.json());
      } catch { /* ignore */ }
    })();
  }, []);

  if (!info) return (
    <Card data-testid="referral-card"><CardContent className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /></CardContent></Card>
  );

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(info.code);
      setCopied(true);
      toast.success('Referral code copied');
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error('Could not copy — long-press to select'); }
  };

  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: 'CognitiaIndia', text: info.share_message, url: info.share_url }); }
      catch { /* user cancelled */ }
    } else {
      copyCode();
    }
  };

  return (
    <Card className="border-fuchsia-500/30 bg-gradient-to-br from-fuchsia-500/5 via-background to-amber-500/5" data-testid="referral-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Gift className="w-4 h-4 text-fuchsia-500" /> Refer a Friend, Both Get a Free Month
          <Badge variant="outline" className="text-[9px] ml-auto">
            {info.bonus_months_earned > 0 ? `+${info.bonus_months_earned} month${info.bonus_months_earned !== 1 ? 's' : ''} earned` : 'No bonuses yet'}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-muted-foreground mb-3">
          Share your code with a friend. When they subscribe using it, <b>both of you</b> get one free month added instantly.
        </p>
        <div className="flex items-center gap-2 mb-3">
          <div className="flex-1 px-3 py-2 rounded-lg border border-border bg-background font-mono text-sm font-bold" data-testid="referral-code">{info.code}</div>
          <Button size="sm" variant="outline" onClick={copyCode} data-testid="referral-copy-btn">
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          </Button>
          <Button size="sm" onClick={share} data-testid="referral-share-btn">Share</Button>
        </div>
        <p className="text-[10px] text-muted-foreground">Your friend applies the code on their signup screen. Bonus kicks in the moment they complete their first payment.</p>
      </CardContent>
    </Card>
  );
}
